<script lang="ts">
  import { onMount } from "svelte";
  import TextInputSheet from "../components/TextInputSheet.svelte";
  import { api, type ManagerCard } from "../lib/api";
  import { haptic } from "../lib/tg";

  let { amoUserId, onback, onhome }: { amoUserId: number; onback?: () => void; onhome?: () => void } = $props();

  let card = $state<ManagerCard | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let msgSheetOpen = $state(false);
  let toast = $state<string | null>(null);
  let tab = $state<"tasks" | "deals">("tasks");
  let removing = $state<number | null>(null);
  let menuFor = $state<number | null>(null);

  async function completeTask(taskAmoId: number) {
    removing = taskAmoId;
    menuFor = null;
    try {
      await api.completeTask(taskAmoId);
      if (card) {
        card.open_tasks = card.open_tasks.filter((t) => t.amo_id !== taskAmoId);
        card.totals = { ...card.totals, open_tasks: Math.max(0, card.totals.open_tasks - 1) };
      }
    } catch {}
    finally { removing = null; }
  }
  async function deleteTask(taskAmoId: number, text: string) {
    if (!confirm(`Удалить задачу?\n\n«${text.slice(0, 80)}»\n\nЗадача будет закрыта в amoCRM с пометкой «Удалено через Квин» и исчезнет из всех списков.`)) return;
    removing = taskAmoId;
    menuFor = null;
    try {
      await api.deleteTask(taskAmoId);
      if (card) {
        card.open_tasks = card.open_tasks.filter((t) => t.amo_id !== taskAmoId);
        card.totals = { ...card.totals, open_tasks: Math.max(0, card.totals.open_tasks - 1) };
      }
    } catch (e) {
      alert(`Не удалось удалить: ${e instanceof Error ? e.message : "ошибка"}`);
    } finally { removing = null; }
  }

  function showToast(t: string, ms = 3000) {
    toast = t;
    setTimeout(() => { if (toast === t) toast = null; }, ms);
  }

  async function sendDirectMessage(text: string) {
    haptic("medium");
    try {
      await api.managerMessage(amoUserId, text);
      showToast(`Отправлено ${card?.name ?? "менеджеру"}`);
    } catch (e) {
      showToast(`${e instanceof Error ? e.message : "ошибка"}`);
    }
  }

  function moneyFmt(rub: number): string {
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(1).replace(/\.0$/, "")} млн ₽`;
    if (rub >= 1000) return `${Math.floor(rub / 1000)}к ₽`;
    return `${rub} ₽`;
  }

  function dueFmt(iso: string | null, overdueDays: number): string {
    if (!iso) return "—";
    if (overdueDays > 0) return `просрочено на ${overdueDays} дн`;
    const d = new Date(iso);
    const delta = (d.getTime() - Date.now()) / 1000;
    if (delta < 0) {
      const overdueH = Math.floor(-delta / 3600);
      if (overdueH >= 1) return `просрочено ${overdueH}ч`;
      const overdueM = Math.max(1, Math.floor(-delta / 60));
      return `просрочено ${overdueM} мин`;
    }
    if (delta < 3600) return `через ${Math.floor(delta / 60)} мин`;
    if (delta < 86400) return `через ${Math.floor(delta / 3600)}ч`;
    return d.toLocaleDateString("ru", { day: "numeric", month: "short" });
  }

  onMount(async () => {
    try {
      card = await api.managerCard(amoUserId);
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">Менеджер</div>
    <button class="hdr-icon" aria-label="Поиск"
            onclick={() => window.dispatchEvent(new Event("open-search"))}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    </button>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {/if}
  </header>

  {#if loading}
    <div class="loading">Загружаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if card}
    <section class="hero">
      <div class="hero-top">
        <div class="hero-info">
          <div class="name">{card.name}</div>
          {#if card.email}<div class="email">{card.email}</div>{/if}
          {#if card.is_admin}<div class="badge">админ amoCRM</div>{/if}
        </div>
        <button class="msg-btn" onclick={() => (msgSheetOpen = true)} aria-label="Написать сообщение">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          Написать
        </button>
      </div>
    </section>

    <section class="totals">
      <div class="t-card">
        <div class="t-label">сделок</div>
        <div class="t-val">{card.totals.deals}</div>
      </div>
      <div class="t-card accent">
        <div class="t-label">в воронке</div>
        <div class="t-val">{moneyFmt(card.totals.deal_value_rub)}</div>
      </div>
      <div class="t-card" class:warn={card.totals.open_tasks > 0}>
        <div class="t-label">задач</div>
        <div class="t-val">{card.totals.open_tasks}</div>
      </div>
      <div class="t-card" class:danger={card.totals.overdue > 0}>
        <div class="t-label">просрочек</div>
        <div class="t-val">{card.totals.overdue}</div>
      </div>
    </section>

    <div class="tabs">
      <button class="tab" class:active={tab === "tasks"} onclick={() => (tab = "tasks")}>
        Задачи · {card.open_tasks.length}
      </button>
      <button class="tab" class:active={tab === "deals"} onclick={() => (tab = "deals")}>
        Сделки · {card.deals.length}
      </button>
    </div>

    {#if tab === "tasks"}
      {#if card.open_tasks.length === 0}
        <div class="empty">Открытых задач нет</div>
      {:else}
      <section class="list">
        {#each card.open_tasks as t}
          {@const href = t.entity_type === "leads" && t.entity_amo_id ? `?deal=${t.entity_amo_id}` : null}
          <div class="row-wrap">
            {#if href}
              <a class="row" class:overdue={t.overdue_days > 0} href={href}>
                {#if t.overdue_days > 0}
                  <div class="ovd-badge">{t.overdue_days}<span class="ovd-unit">дн</span></div>
                {/if}
                <div class="row-main">
                  <div class="row-text">{t.text}</div>
                  {#if t.lead_name}
                    <div class="row-lead">по сделке: {t.lead_name}</div>
                  {/if}
                  <div class="row-due">{dueFmt(t.complete_till, t.overdue_days)}</div>
                </div>
              </a>
            {:else}
              <div class="row floating" class:overdue={t.overdue_days > 0}>
                {#if t.overdue_days > 0}
                  <div class="ovd-badge">{t.overdue_days}<span class="ovd-unit">дн</span></div>
                {/if}
                <div class="row-main">
                  <div class="row-text">{t.text}</div>
                  <div class="row-lead floating-tag">без сделки</div>
                  <div class="row-due">{dueFmt(t.complete_till, t.overdue_days)}</div>
                </div>
              </div>
            {/if}
            <button class="kebab" type="button" aria-label="Действия"
                    disabled={removing === t.amo_id}
                    onclick={(e) => { e.stopPropagation(); menuFor = menuFor === t.amo_id ? null : t.amo_id; }}>
              {#if removing === t.amo_id}⋯{:else}⋮{/if}
            </button>
            {#if menuFor === t.amo_id}
              <div class="menu" onclick={(e) => e.stopPropagation()} role="menu">
                <button onclick={() => completeTask(t.amo_id)} type="button">Снять (выполнить)</button>
                <button onclick={() => deleteTask(t.amo_id, t.text)} type="button" class="danger">Удалить</button>
                <button onclick={() => menuFor = null} type="button" class="ghost">Отмена</button>
              </div>
            {/if}
          </div>
        {/each}
      </section>
      {/if}
    {:else if tab === "deals"}
      {#if card.deals.length === 0}
        <div class="empty">Активных сделок нет</div>
      {:else}
      <section class="list">
        {#each card.deals as d}
          <a class="row deal" class:silent={d.silence_days >= 7} href={`?deal=${d.amo_id}`}>
            <div class="row-top">
              <div class="row-text">{d.name}</div>
              {#if d.price > 0}<div class="row-price">{moneyFmt(d.price)}</div>{/if}
            </div>
            <div class="row-meta">
              {d.stage_name} <span class="sep"></span>
              {#if d.silence_days >= 7}
                <span class="silent-tag">молчит {d.silence_days} дн</span>
              {:else if d.silence_days > 0}
                обновлена {d.silence_days} дн назад
              {:else}
                обновлена сегодня
              {/if}
            </div>
          </a>
        {/each}
      </section>
      {/if}
    {/if}
  {/if}

  <TextInputSheet bind:open={msgSheetOpen} onsubmit={sendDirectMessage} />

  {#if toast}
    <div class="mv-toast">{toast}</div>
  {/if}
</div>

<style>
  .screen { padding-bottom: 100px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home, .hdr-icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
    background: transparent; border: none; cursor: pointer;
  }
  .back:active, .home:active, .hdr-icon:active { background: var(--bg-elev); }
  .back svg, .home svg, .hdr-icon svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 16px; font-weight: 700;
    flex: 1; text-align: center;
  }
  .spacer { width: 36px; }

  .loading, .error-box {
    padding: 40px 16px; text-align: center;
    color: var(--text-mute); font-size: 13px;
  }
  .error-box { color: var(--danger); }

  .hero { padding: 16px; }
  .hero-top { display: flex; align-items: flex-start; gap: 12px; }
  .hero-info { flex: 1; min-width: 0; }
  .msg-btn {
    flex-shrink: 0;
    display: flex; align-items: center; gap: 6px;
    padding: 8px 12px;
    background: var(--bg-elev-2);
    border: 1px solid var(--ember);
    border-radius: 10px;
    color: var(--ember);
    font-family: 'Manrope', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms;
  }
  .msg-btn:active { background: var(--ember); color: #fff; }
  .msg-btn svg { width: 16px; height: 16px; }
  .mv-toast {
    position: fixed; left: 50%; top: calc(60px + var(--safe-top, 0px));
    transform: translateX(-50%);
    background: var(--bg-elev);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 10px 16px;
    font-size: 13px; font-weight: 500;
    box-shadow: var(--shadow-lg);
    z-index: 100;
    max-width: 88%; text-align: center;
  }
  .name {
    font-family: 'Manrope', sans-serif;
    font-size: 20px; font-weight: 700;
  }
  .email {
    margin-top: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: var(--text-mute);
  }
  .badge {
    display: inline-block;
    margin-top: 8px;
    padding: 3px 8px;
    background: var(--bg-elev-2);
    border: 1px solid var(--brass);
    border-radius: 999px;
    font-size: 10px;
    color: var(--brass);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .totals {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 4px 16px 12px;
  }
  .t-card {
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: var(--shadow-sm);
  }
  .t-card.accent .t-val { color: var(--brass); }
  .t-card.warn .t-val { color: var(--warning); }
  .t-card.danger .t-val { color: var(--danger); }
  .t-label {
    font-size: 11px;
    color: var(--text-dim);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .t-val {
    font-family: 'JetBrains Mono', monospace;
    font-size: 20px;
    font-weight: 700;
    margin-top: 4px;
    letter-spacing: -0.02em;
  }

  .section-title {
    padding: 18px 16px 8px;
    font-family: 'Manrope', sans-serif;
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-dim);
  }

  .tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 4px 16px 12px;
  }
  .tab {
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 22px;
    padding: 11px 14px;
    color: var(--text);
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
  }
  .tab:active { transform: scale(0.97); }
  .tab.active {
    background: var(--ember);
    color: #fff;
    border-color: var(--ember);
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(232,124,47,0.32);
  }

  .empty {
    padding: 40px 16px;
    text-align: center;
    color: var(--text-mute);
    font-size: 13px;
  }

  .list { padding: 12px 16px 0; display: flex; flex-direction: column; gap: 8px; }
  .row {
    display: block;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
    transition: transform 80ms ease;
  }
  .row:active { transform: scale(0.985); }
  .row.overdue { border-left: 3px solid var(--danger); }
  .row.silent { border-left: 3px solid var(--warning); }
  .row-text {
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 14px;
  }
  .row-lead {
    margin-top: 4px;
    font-size: 12px;
    color: var(--brass);
    font-weight: 600;
  }
  .row-due {
    margin-top: 4px;
    font-size: 12px;
    color: var(--text-mute);
  }
  .row.overdue .row-due { color: var(--danger); font-weight: 600; }

  .row.deal .row-top {
    display: flex; justify-content: space-between; align-items: center;
    gap: 10px;
  }
  .row-price {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: var(--brass);
    flex-shrink: 0;
  }
  .row-meta {
    margin-top: 4px;
    font-size: 11px;
    color: var(--text-mute);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .sep {
    width: 3px; height: 3px;
    background: var(--text-dim);
    border-radius: 50%;
  }
  .silent-tag { color: var(--warning); font-weight: 600; }
</style>
